import { NavLink, Link, Outlet, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, Home as HomeIcon, MoreHorizontal } from "lucide-react";
import "@/marketing.css";

const NAV = [
  { to: "/",         label: "Home"     },
  { to: "/services", label: "Services" },
  { to: "/reviews",  label: "Reviews"  },
  { to: "/about",    label: "About"    },
  { to: "/contact",  label: "Contact"  },
];

function Header() {
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuRef = useRef(null);
  const loc = useLocation();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => setMenuOpen(false), [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all ${
        scrolled ? "bg-white/85 backdrop-blur border-b border-[var(--mkt-line)]" : "bg-transparent"
      }`}
      data-testid="mkt-header"
    >
      <div className="mkt-container flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2 font-bold text-lg" data-testid="mkt-logo">
          <span className="inline-block w-8 h-8 rounded-full bg-[var(--mkt-black)] text-white grid place-items-center">
            <HomeIcon size={16} strokeWidth={2.3} />
          </span>
          <span className="mkt-display">FlipPilot</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              className={({ isActive }) =>
                `mkt-link text-sm ${isActive ? "active" : ""}`
              }
              data-testid={`mkt-nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/contact"
            className="hidden md:inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-[var(--mkt-black)] text-white hover:bg-[var(--mkt-accent)] transition-colors whitespace-nowrap"
            data-testid="mkt-nav-cta"
          >
            Get Cash Offer
          </Link>
          {/* Desktop overflow menu. Houses Partner Login without cluttering the nav */}
          <div className="relative hidden md:block" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More options"
              aria-expanded={menuOpen}
              data-testid="mkt-desktop-menu-toggle"
              className={`p-2 rounded-lg border transition-colors ${
                menuOpen
                  ? "border-[var(--mkt-black)] bg-[var(--mkt-surface)]"
                  : "border-[var(--mkt-line)] hover:border-[var(--mkt-black)]"
              }`}
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-44 bg-white border border-[var(--mkt-line)] rounded-lg shadow-lg overflow-hidden z-50"
                data-testid="mkt-desktop-menu"
              >
                <Link
                  to="/partner"
                  onClick={() => setMenuOpen(false)}
                  data-testid="desktop-partner-login"
                  className="block px-4 py-2.5 text-sm hover:bg-[var(--mkt-surface)] transition-colors"
                >
                  Partner Login
                </Link>
              </div>
            )}
          </div>
          <button
            className="md:hidden p-2 rounded-md border border-[var(--mkt-line)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mkt-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-[var(--mkt-line)]" data-testid="mkt-mobile-menu">
          <div className="mkt-container py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `py-2 border-b border-[var(--mkt-line)] ${isActive ? "font-semibold" : ""}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mkt-btn mkt-btn-primary w-full justify-center">
              Get Cash Offer
            </Link>
            <Link
              to="/partner"
              className="mkt-btn mkt-btn-ghost w-full justify-center mt-1"
              data-testid="mobile-partner-login"
            >
              Partner Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[var(--mkt-black)] text-white pt-20 pb-10 mt-24" data-testid="mkt-footer">
      <div className="mkt-container grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="mkt-display text-2xl mb-3">FlipPilot</div>
          <p className="text-white/70 text-sm max-w-md leading-relaxed">
            Buying houses across Southwest Florida for cash. Fast, fair, and
            hassle-free. Written offer in 24 hours, close in as little as
            14 days, zero fees or commissions on your side.
          </p>
          <div className="mt-6 flex gap-2 flex-wrap">
            <Link to="/contact" className="mkt-btn mkt-btn-accent text-sm">
              Get Cash Offer
            </Link>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="hover:text-[var(--mkt-accent-2)]">About</Link></li>
            <li><Link to="/services" className="hover:text-[var(--mkt-accent-2)]">Services</Link></li>
            <li><Link to="/reviews" className="hover:text-[var(--mkt-accent-2)]">Reviews</Link></li>
            <li><Link to="/contact" className="hover:text-[var(--mkt-accent-2)]">Contact</Link></li>
            <li><Link to="/privacy" className="hover:text-[var(--mkt-accent-2)]" data-testid="footer-privacy-link">Privacy Policy</Link></li>
            <li><Link to="/terms" className="hover:text-[var(--mkt-accent-2)]" data-testid="footer-terms-link">Terms &amp; Conditions</Link></li>
          </ul>
        </div>

        <div>
          <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Contact</div>
          <ul className="space-y-2 text-sm text-white/80">
            <li>Southwest Florida</li>
            <li data-testid="footer-phone">
              <span className="text-white/50">Phone:</span>{" "}
              <a href="tel:+12394808400" className="hover:text-[var(--mkt-accent-2)]">
                (239) 480-8400
              </a>
            </li>
            <li data-testid="footer-email">
              <span className="text-white/50">Email:</span>{" "}
              <a href="mailto:sales@flippilot.us" className="hover:text-[var(--mkt-accent-2)]">
                sales@flippilot.us
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="mkt-container mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-2 text-xs text-white/50">
        <div>© {new Date().getFullYear()} FlipPilot. All rights reserved.</div>
        <div>Made with ❤ in Southwest Florida.</div>
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="marketing-scope min-h-screen flex flex-col" data-testid="marketing-scope">
      <Header />
      <main className="flex-1 pt-16 md:pt-20">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
